import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Button, Tag, Row, Col, Table, Input, Select, Spin, Empty, message, Modal, Tooltip } from "antd";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  SearchOutlined,
  EyeOutlined,
  EditOutlined,
  CheckOutlined,
  CloseOutlined,
  ExclamationCircleOutlined,
  LinkOutlined,
  TeamOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import styled from "styled-components";
import axios from "axios";
import { API_CONST } from "../../../const";

const { Option } = Select;
const { TextArea } = Input;

const PageHeader = styled.div`
  margin-bottom: 24px;
  h1 { font-size: 24px; font-weight: 600; color: #014c75; margin: 0 0 8px 0; }
  p { color: #666; margin: 0; font-size: 14px; }
`;

const AdminProjectsWrapper = styled.div`
  min-height: 100vh;

  .metrics-row {
    margin-bottom: 24px;

    .metric-card {
      border-radius: 12px;
      transition: all 0.3s ease;

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      }

      .metric-content {
        display: flex;
        align-items: center;
        gap: 16px;

        .metric-icon {
          width: 56px;
          height: 56px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .metric-info {
          .metric-value {
            font-size: 28px;
            font-weight: 700;
            color: #014c75;
            margin: 0;
            line-height: 1.2;
          }

          .metric-label {
            font-size: 14px;
            color: #8c8c8c;
            margin: 0;
          }
        }
      }
    }
  }

  .tabs-section {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 24px;
    padding-bottom: 16px;
    border-bottom: 1px solid #f0f0f0;

    .tab-button {
      border: none;
      background: transparent;
      padding: 8px 20px;
      border-radius: 8px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.3s ease;

      &.active {
        background: #01d9a9;
        color: white;
      }

      &:hover:not(.active) {
        background: #f0f0f0;
      }
    }
  }

  .filters-section {
    display: flex;
    gap: 12px;
    margin-bottom: 24px;
    flex-wrap: wrap;
  }

  .project-title-link {
    color: #014c75;
    font-weight: 500;
    cursor: pointer;

    &:hover {
      color: #01d9a9;
    }
  }

  .org-link {
    color: #1890ff;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }

  .action-buttons {
    display: flex;
    gap: 8px;
  }
`;

const statusColors = {
  pending_approval: "orange",
  active: "green",
  rejected: "red",
  completed: "default",
};

const statusLabels = {
  pending_approval: "Pending Approval",
  active: "Active",
  rejected: "Rejected",
  completed: "Completed",
};

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") return "₹0";
  return `₹ ${parseFloat(value).toLocaleString("en-IN")}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const AdminProjects = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("active");
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [stats, setStats] = useState({
    total: 0,
    pendingApproval: 0,
    active: 0,
    rejected: 0,
    completed: 0,
  });
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const status = activeTab;
      
      const response = await axios.post(
        API_CONST.GET_PROJECTS,
        { status, search: searchText, limit: 100 },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setProjects(response.data.projects || []);
    } catch (error) {
      console.error("Error fetching projects:", error);
      message.error("Failed to load projects");
    } finally {
      setLoading(false);
    }
  }, [activeTab, searchText]);

  const fetchStats = useCallback(async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        API_CONST.GET_PROJECT_STATS,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStats(response.data.stats || {});
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
    fetchStats();
  }, [fetchProjects, fetchStats]);

  const handleViewDetails = (id) => {
    navigate(`/admin/projects/details/${id}`);
  };

  const handleApprove = async (project) => {
    Modal.confirm({
      title: "Approve Project",
      icon: <CheckCircleOutlined style={{ color: "#52c41a" }} />,
      content: `Are you sure you want to approve "${project.title}"? This will activate the project.`,
      okText: "Approve",
      okButtonProps: { style: { background: "#52c41a", borderColor: "#52c41a" } },
      onOk: async () => {
        setActionLoading(true);
        try {
          const token = localStorage.getItem("authToken");
          await axios.post(
            `${API_CONST.APPROVE_PROJECT}/${project.id}/approve`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
          message.success("Project approved successfully");
          fetchProjects();
          fetchStats();
        } catch (error) {
          message.error(error.response?.data?.error || "Failed to approve project");
        } finally {
          setActionLoading(false);
        }
      },
    });
  };

  const handleRejectClick = (project) => {
    setSelectedProject(project);
    setRejectionReason("");
    setRejectModalVisible(true);
  };

  const handleRejectSubmit = async () => {
    if (!rejectionReason.trim()) {
      message.error("Please provide a rejection reason");
      return;
    }

    setActionLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      await axios.post(
        `${API_CONST.REJECT_PROJECT}/${selectedProject.id}/reject`,
        { reason: rejectionReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success("Project rejected");
      setRejectModalVisible(false);
      setSelectedProject(null);
      setRejectionReason("");
      fetchProjects();
      fetchStats();
    } catch (error) {
      message.error(error.response?.data?.error || "Failed to reject project");
    } finally {
      setActionLoading(false);
    }
  };

  const handleComplete = async (project) => {
    Modal.confirm({
      title: "Complete Project",
      icon: <ExclamationCircleOutlined style={{ color: "#faad14" }} />,
      content: `Are you sure you want to mark "${project.title}" as completed?`,
      okText: "Complete",
      onOk: async () => {
        setActionLoading(true);
        try {
          const token = localStorage.getItem("authToken");
          await axios.post(
            `${API_CONST.COMPLETE_PROJECT}/${project.id}/complete`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
          message.success("Project marked as completed");
          fetchProjects();
          fetchStats();
        } catch (error) {
          message.error(error.response?.data?.error || "Failed to complete project");
        } finally {
          setActionLoading(false);
        }
      },
    });
  };

  const tableColumns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 150,
      render: (id) => <span style={{ fontSize: 12, color: "#8c8c8c" }}>{id}</span>,
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      render: (title, record) => (
        <span className="project-title-link" onClick={() => handleViewDetails(record.id)}>
          {title}
        </span>
      ),
    },
    {
      title: "Engagement",
      key: "engagement",
      width: 200,
      render: (_, record) => (
        <span 
          className="org-link"
          onClick={() => navigate(`/admin/engagement/${record.engagementId}`)}
        >
          <LinkOutlined /> {record.engagementId}
        </span>
      ),
    },
    {
      title: "Organization",
      key: "organization",
      width: 180,
      render: (_, record) => record.organizationName ? (
        <span 
          className="org-link"
          onClick={() => navigate(`/admin/organization/${record.organizationId}`)}
        >
          {record.organizationName}
        </span>
      ) : "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 140,
      render: (status) => <Tag color={statusColors[status]}>{statusLabels[status]}</Tag>,
    },
    {
      title: "Country",
      dataIndex: "country",
      key: "country",
      width: 130,
      render: (v) => v || "-",
    },
    {
      title: "Budget",
      dataIndex: "budgetAllocated",
      key: "budgetAllocated",
      width: 150,
      render: (budget) => formatCurrency(budget),
    },
    {
      title: "Jobs",
      dataIndex: "jobCount",
      key: "jobCount",
      width: 70,
      align: "center",
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 120,
      render: (date) => formatDate(date),
    },
    {
      title: "Action",
      key: "action",
      width: 180,
      fixed: "right",
      render: (_, record) => (
        <div className="action-buttons">
          <Tooltip title="View Details">
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetails(record.id)}
            />
          </Tooltip>
          {record.status === "active" && (
            <>
              <Tooltip title="Edit Project">
                <Button
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => navigate(`/admin/projects/edit/${record.id}`)}
                />
              </Tooltip>
              <Tooltip title="Mark Complete">
                <Button
                  size="small"
                  icon={<CheckCircleOutlined />}
                  onClick={() => handleComplete(record)}
                  loading={actionLoading}
                />
              </Tooltip>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <AdminProjectsWrapper>
      <PageHeader><h1>Projects</h1><p>Manage and track project details</p></PageHeader>

      <Row gutter={16} className="metrics-row">
        <Col xs={24} sm={6}>
          <Card className="metric-card" hoverable onClick={() => setActiveTab("active")}>
            <div className="metric-content">
              <div className="metric-icon" style={{ background: "#f6ffed" }}>
                <CheckCircleOutlined style={{ color: "#52c41a", fontSize: 24 }} />
              </div>
              <div className="metric-info">
                <h3 className="metric-value">{stats.active || 0}</h3>
                <p className="metric-label">Active Projects</p>
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card className="metric-card" hoverable onClick={() => setActiveTab("pending_approval")}>
            <div className="metric-content">
              <div className="metric-icon" style={{ background: "#fff7e6" }}>
                <ClockCircleOutlined style={{ color: "#fa8c16", fontSize: 24 }} />
              </div>
              <div className="metric-info">
                <h3 className="metric-value">{stats.pendingApproval || 0}</h3>
                <p className="metric-label">Pending Approval</p>
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card className="metric-card" hoverable>
            <div className="metric-content">
              <div className="metric-icon" style={{ background: "#e6f7ff" }}>
                <TeamOutlined style={{ color: "#1890ff", fontSize: 24 }} />
              </div>
              <div className="metric-info">
                <h3 className="metric-value">{stats.avgTalentsPerProject || 0}</h3>
                <p className="metric-label">Avg Talents/Project</p>
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card className="metric-card" hoverable>
            <div className="metric-content">
              <div className="metric-icon" style={{ background: "#f0f5ff" }}>
                <DollarOutlined style={{ color: "#597ef7", fontSize: 24 }} />
              </div>
              <div className="metric-info">
                <h3 className="metric-value">{formatCurrency(stats.avgSpentPerProject, true)}</h3>
                <p className="metric-label">Avg Spent/Project</p>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      <div className="tabs-section">
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            className={`tab-button ${activeTab === "active" ? "active" : ""}`}
            onClick={() => setActiveTab("active")}
          >
            Active ({stats.active || 0})
          </Button>
          <Button
            className={`tab-button ${activeTab === "completed" ? "active" : ""}`}
            onClick={() => setActiveTab("completed")}
          >
            Completed ({stats.completed || 0})
          </Button>
          <Button
            className={`tab-button ${activeTab === "rejected" ? "active" : ""}`}
            onClick={() => setActiveTab("rejected")}
          >
            Rejected ({stats.rejected || 0})
          </Button>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate("/admin/projects/create")}
        >
          Create Project
        </Button>
      </div>

      <div className="filters-section">
        <Input
          prefix={<SearchOutlined />}
          placeholder="Search projects..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: 300 }}
          allowClear
        />
      </div>

      <Spin spinning={loading}>
        <Table
          columns={tableColumns}
          dataSource={projects}
          rowKey="id"
          pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ["10", "20", "50", "100"], showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} rows` }}
          scroll={{ x: 1200 }}
          locale={{ emptyText: <Empty description="No projects found" /> }}
        />
      </Spin>

      <Modal
        title="Reject Project"
        open={rejectModalVisible}
        onCancel={() => setRejectModalVisible(false)}
        onOk={handleRejectSubmit}
        okText="Reject"
        okButtonProps={{ danger: true, loading: actionLoading }}
      >
        <p>Please provide a reason for rejecting "{selectedProject?.title}":</p>
        <TextArea
          rows={4}
          value={rejectionReason}
          onChange={(e) => setRejectionReason(e.target.value)}
          placeholder="Enter rejection reason..."
        />
      </Modal>
    </AdminProjectsWrapper>
  );
};

export default AdminProjects;
